'use client';

import { createFileRoute } from '@tanstack/react-router';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { constructQuery } from '@repo/shared/helpers';
import { PERMISSIONS, Tag, Type } from '@repo/shared/domain';
import { getVerifyPermission } from '@repo/shared/helpers';
import EventsTab from 'src/parts/tabs/EventsTab';
import { useAuthenticatedUser } from 'src/hooks/useAuthenticatedUser';
import { useConfig } from 'src/hooks/useConfig';
import { getGroupEvents } from 'src/api/personal/groupDetail';
import { createEvent } from 'src/api/personal/events';
import { getEventTypes } from 'src/api/admin/types';
import { getProjects } from 'src/api/personal/projects';
import { getAllTags } from 'src/api/admin/tags';

interface EventsSearch {
	page?: number;
	sort?: string;
}

export const Route = createFileRoute('/personal_/groups/$groupId/events')({
	validateSearch: (search: Record<string, unknown>): EventsSearch => ({
		page: (search.page as number) || undefined,
		sort: (search.sort as string) || undefined,
	}),
	loaderDeps: ({ search }) => search,
	loader: async ({ params, deps }) => {
		const query = constructQuery({ page: deps.page, sort: deps.sort });
		const [events, types, projects] = await Promise.all([
			getGroupEvents(params.groupId, query),
			getEventTypes(),
			getProjects(),
		]);
		return {
			events,
			types: types?.data ?? [],
			projects: projects?.data ?? [],
		};
	},
	component: GroupEventsPage,
});

function GroupEventsPage() {
	const { groupId } = Route.useParams();
	const searchParams = Route.useSearch();
	const queryClient = useQueryClient();
	const { events: initialEvents, types: initialTypes } = Route.useLoaderData();
	const { user: authenticatedUser } = useAuthenticatedUser();
	const { config } = useConfig();
	const featureFlags = config?.featureFlags ?? {};
	const usesSubEvents = featureFlags?.USES_SUBCONTRACTING ?? false;
	const usesTags = featureFlags?.USES_TAGS ?? false;

	const verifyPermission = getVerifyPermission(authenticatedUser?.permissions ?? []);

	const query = constructQuery({ page: searchParams.page, sort: searchParams.sort });
	const { data: events, refetch: refetchEvents } = useQuery({
		queryKey: ['personal-group-events', groupId, searchParams.page, searchParams.sort],
		queryFn: () => getGroupEvents(groupId, query),
		initialData: initialEvents,
	});

	const { data: typesData } = useQuery({
		queryKey: ['personal-event-types'],
		queryFn: () => getEventTypes(),
	});

	const { data: tagsData } = useQuery({
		queryKey: ['tags'],
		queryFn: () => getAllTags(),
		enabled: usesTags,
	});

	const handleReload = () => {
		refetchEvents();
		queryClient.invalidateQueries({ queryKey: ['personal-group', groupId] });
	};

	const addEvent = async (data: Record<string, unknown>) => {
		await createEvent(data);
	};

	const types: Type[] = typesData?.data ?? initialTypes;
	const tags: Tag[] = tagsData ?? [];

	return (
		<EventsTab
			authenticatedUser={authenticatedUser!}
			events={events}
			groupId={groupId}
			addEvent={addEvent}
			onReload={handleReload}
			types={types}
			tags={tags}
			permissions={{
				view: verifyPermission([PERMISSIONS.personalGroupsEvents]),
				create: verifyPermission([PERMISSIONS.personalGroupsEvents]),
				update: verifyPermission([PERMISSIONS.personalGroupsEdit]),
				archive: verifyPermission([PERMISSIONS.personalGroupsEdit]),
			}}
			singleGroupAsOwner={usesSubEvents}
			featureFlags={featureFlags}
			eventDetailUrl="/personal/events"
			projectGetUrl="/api/personal-dashboard/projects/"
		/>
	);
}
